/**
 * Done. / ChiroCPD — ActivityRow Feature Component
 *
 * Table row (desktop) / card (mobile) for a single CPD activity.
 * Expand → inline AIReflectionPanel.
 * Edit and Delete actions with confirmation.
 */

import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { AIReflectionPanel } from '@/components/features/AIReflectionPanel';
import { formatDate, formatHours } from '@/lib/utils';
import type { Activity, Reflection } from '@/lib/types';

interface ActivityRowProps {
  activity: Activity;
  onEdit: (activity: Activity) => void;
  onDelete: (id: string) => Promise<void>;
  onReflectionSaved?: ((reflection: Reflection) => void) | undefined;
}

export function ActivityRow({
  activity,
  onEdit,
  onDelete,
  onReflectionSaved,
}: ActivityRowProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const expandId = `activity-expand-${activity.id}`;

  const handleDelete = useCallback(async (): Promise<void> => {
    if (!window.confirm(`Delete "${activity.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await onDelete(activity.id);
    } catch {
      setDeleting(false);
    }
  }, [activity.id, activity.title, onDelete]);

  const reflectionStatus = activity.hasReflection ? 'complete' : 'empty';

  return (
    <div className="border-b border-[var(--border-subtle)] last:border-0">
      {/* Main row */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-3 px-4">
        {/* Date */}
        <time
          dateTime={activity.activityDate}
          className="text-xs text-[var(--text-muted)] shrink-0 w-24"
        >
          {formatDate(activity.activityDate)}
        </time>

        {/* Type badge */}
        <div className="shrink-0">
          <StatusBadge status="complete" label={activity.activityTypeName} />
        </div>

        {/* Title */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[var(--text-primary)] truncate">
            {activity.title}
          </p>
          {activity.description && (
            <p className="text-xs text-[var(--text-muted)] truncate">{activity.description}</p>
          )}
        </div>

        {/* Hours */}
        <span className="text-sm font-semibold text-[var(--text-primary)] shrink-0">
          {formatHours(activity.hours)} hrs
        </span>

        {/* Reflection status */}
        <div className="shrink-0">
          <StatusBadge
            status={reflectionStatus}
            label={activity.hasReflection ? 'Reflected' : 'No reflection'}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { onEdit(activity); }}
            ariaLabel={`Edit ${activity.title}`}
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { void handleDelete(); }}
            ariaDisabled={deleting}
            ariaLabel={`Delete ${activity.title}`}
            className="text-red-600 hover:bg-[var(--color-error-100)]"
          >
            {deleting ? '…' : 'Delete'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setExpanded(e => !e); }}
            aria-expanded={expanded}
            aria-controls={expandId}
            ariaLabel={expanded ? 'Collapse reflection' : 'Expand reflection'}
          >
            {expanded ? '▲' : '▼'}
          </Button>
        </div>
      </div>

      {/* Expandable reflection panel */}
      {expanded && (
        <div id={expandId} className="px-4 pb-4">
          <AIReflectionPanel
            activityId={activity.id}
            activityTitle={activity.title}
            onSaved={onReflectionSaved}
          />
        </div>
      )}
    </div>
  );
}
